import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useAccountVesselScope } from '../../hooks/useAccountVesselScope';
import { Button } from '../../components/ui/Button';
import { cn } from '../../lib/utils';
import { createSpareDeliveryFromPurchaseOrder } from '../../services/mutations';
import { useVesselSparePendingPurchaseOrderDetail } from './useData';

const Detail: React.FC = () => {
  const { purchaseOrderId } = useParams<{ purchaseOrderId: string }>();
  const vid = useAccountVesselScope() ?? NaN;
  const poId = purchaseOrderId ? Number(purchaseOrderId) : NaN;
  const { colors } = useTheme();
  const { can } = useAuth();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const { data, isLoading, isError, mutate } = useVesselSparePendingPurchaseOrderDetail(
    Number.isFinite(poId) ? poId : undefined,
    Number.isFinite(vid) ? vid : undefined,
  );

  const canCreateDelivery = can('vessel_spare_pending_deliveries', 'edit');

  const handleCreateDelivery = async () => {
    if (!Number.isFinite(vid) || !Number.isFinite(poId)) return;
    setBusy(true);
    try {
      await createSpareDeliveryFromPurchaseOrder({
        purchaseOrderId: poId,
        vesselId: vid,
      });
      showSuccess('Delivery created');
      await mutate();
      navigate(`/spare-pending-deliveries`);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      showError(msg || 'Could not create delivery');
    } finally {
      setBusy(false);
    }
  };

  if (!Number.isFinite(vid) || !Number.isFinite(poId)) {
    return <p className="text-sm text-red-600 dark:text-red-400">Invalid link.</p>;
  }

  if (isLoading) {
    return <p className={cn('text-sm', colors.textMuted)}>Loading…</p>;
  }

  if (isError || !data) {
    return (
      <div className={cn('rounded-xl border p-6', colors.border, colors.surface)}>
        <p className={cn('text-sm', colors.text)}>This order was not found or has no pending quantity.</p>
        <Link
          to={`/spare-pending-deliveries`}
          className="mt-4 inline-block text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
        >
          Back to pending deliveries
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className={cn('text-xl font-semibold', colors.text)}>Spare pending delivery</h1>
          <p className={cn('text-sm mt-1', colors.textMuted)}>
            Purchase order {data.ref || `#${data.id}`}
            {data.spareCase?.ref ? ` · Case ${data.spareCase.ref}` : ''}
            {data.supplier?.name ? ` · ${data.supplier.name}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/spare-pending-deliveries`}>
            <Button variant="outline" type="button">
              Back to list
            </Button>
          </Link>
          {canCreateDelivery && (
            <Button variant="accent" type="button" disabled={busy} onClick={() => void handleCreateDelivery()}>
              {busy ? 'Creating…' : 'Create delivery'}
            </Button>
          )}
        </div>
      </div>

      <div className={cn('rounded-xl border overflow-hidden', colors.border, colors.surface)}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className={cn('border-b text-left', colors.border, colors.textSecondary)}>
                <th className="px-4 py-3 font-medium">Item</th>
                <th className="px-4 py-3 font-medium text-right">Ordered</th>
                <th className="px-4 py-3 font-medium text-right">Received</th>
                <th className="px-4 py-3 font-medium text-right">Pending</th>
                <th className="px-4 py-3 font-medium">Unit</th>
              </tr>
            </thead>
            <tbody>
              {data.lineItems.map((line) => {
                const label =
                  [line.item?.level1, line.item?.level2, line.item?.level3].filter(Boolean).join(' · ') ||
                  line.itemDescr ||
                  line.itemVcode ||
                  (line.itemId != null ? `Item #${line.itemId}` : '—');
                return (
                  <tr key={line.id ?? `${line.itemId}-${line.itemVcode}`} className={cn('border-b', colors.borderLight)}>
                    <td className={cn('px-4 py-3', colors.text)}>{label}</td>
                    <td className={cn('px-4 py-3 text-right tabular-nums', colors.text)}>{line.orderedQty}</td>
                    <td className={cn('px-4 py-3 text-right tabular-nums', colors.text)}>{line.deliveredQty}</td>
                    <td className={cn('px-4 py-3 text-right tabular-nums font-medium', colors.text)}>
                      {line.pendingQty}
                    </td>
                    <td className={cn('px-4 py-3', colors.textMuted)}>
                      {line.unit?.ucode || line.unit?.descr || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className={cn('text-xs', colors.textMuted)}>
        Amounts and prices are hidden here. Creating a delivery opens the spare delivery record with remaining
        quantities; you can adjust lines there if needed.
      </p>
    </div>
  );
};

export default Detail;
